import { FC, useMemo, useState } from 'react';

import { isEqual } from 'lodash/fp';
import { observer } from 'mobx-react';

import { useStores } from 'mobx/hooks/useStores';

import PatientsFetcher from 'fetchers/PatientsFetcher';

import { DATE_ONLY_FORMAT, formatDate } from 'utils/DateUtils';

import { getFormattedPhoneNumber } from 'utils/PhoneUtils';

import { useTicketClose } from 'hooks/useTicketClose';
import { ResolveTicketsData, useTicketResolve } from 'hooks/useTicketResolve';

import { MergePatientsModalContent } from 'views/Modals/MergePatientsModal/MergePatientModalContent';
import {
  DuplicatePatientData,
  MergePatientsModalColumn
} from 'views/Modals/MergePatientsModal/MergePatientsModal.types';

import { usePatientModel } from 'components/Patient/usePatientModel';
import { useHidePatientDialog } from 'components/Ticket/TicketsContainers/TicketsContainers.shared';

import { Modal } from 'components/UIkit/atoms/Modal';
import { Text } from 'components/UIkit/atoms/Text';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  resolveTicketsData: ResolveTicketsData;
  duplicatePatientData: DuplicatePatientData[];
  action?: 'close' | 'resolve';
}

export const MergeLocalPatientsModal: FC<Props> = observer(
  ({ isOpen, onClose, resolveTicketsData, duplicatePatientData, action = 'resolve' }) => {
    const patient = usePatientModel();
    const { dialog, openDialog } = useHidePatientDialog(onClose);
    const ticketResolve = useTicketResolve();
    const ticketClose = useTicketClose();
    const { careTimerStore } = useStores();
    const [isLoading, setIsLoading] = useState(false);

    const mergePatients = async () => {
      setIsLoading(true);
      try {
        const localPatient = duplicatePatientData.find((patient) => patient.type === 'local');
        const targetPatient = duplicatePatientData.find((patient) => patient.type === 'regular');
        const isLocalPatient = patient?.id === localPatient!.id;

        await careTimerStore.endSession();
        await PatientsFetcher.mergePatients({
          localPatientId: localPatient!.id,
          targetPatientId: targetPatient!.id
        });

        if (action === 'resolve') {
          await ticketResolve(
            resolveTicketsData,
            isLocalPatient ? `/patient/${targetPatient!.id}` : undefined
          );
        } else {
          await ticketClose(
            {
              tickets: resolveTicketsData.tickets,
              patientId: resolveTicketsData.patientId
            },
            isLocalPatient ? `/patient/${targetPatient!.id}` : undefined
          );
        }
      } finally {
        onClose();
        setIsLoading(false);
      }
    };

    const columns: MergePatientsModalColumn[] = useMemo(
      () => [
        {
          accessor: 'type',
          name: '',
          Cell: (cellValue) => (
            <Text key={cellValue} width="200px" textAlign="start" py={12}>
              {cellValue === 'local' ? 'Canopy Record' : 'Practice Management Record'}
            </Text>
          ),
          width: '200px'
        },
        {
          accessor: 'firstName',
          name: 'First Name'
        },
        {
          name: 'Last Name',
          accessor: 'lastName'
        },
        {
          name: 'Date of Birth',
          accessor: 'dateOfBirth',
          formatCell: (cellValue) => (cellValue ? formatDate(cellValue, DATE_ONLY_FORMAT) : '')
        },
        {
          name: 'Creation Date',
          accessor: 'createdAt',
          formatCell: (cellValue) => (cellValue ? formatDate(cellValue, 'M/D/YYYY') : ''),
          compareFn: (cellValue1, cellValue2) =>
            isEqual(
              formatDate(cellValue1, DATE_ONLY_FORMAT),
              formatDate(cellValue2, DATE_ONLY_FORMAT)
            )
        },
        {
          name: 'Phone Number',
          accessor: 'phoneNumber',
          formatCell: (cellValue) => (cellValue ? getFormattedPhoneNumber(cellValue) : '-')
        },
        {
          name: 'MRN',
          accessor: 'mrn'
        }
      ],
      []
    );

    return (
      <>
        {dialog}

        <Modal
          size="large"
          isOpen={isOpen}
          title="Is this the same patient?"
          subtitle={`"${patient?.fullName || 'Patient'}" was registered locally in Canopy.
Please verify whether this patient matches the one in your practice management system (for merging).`}
          confirmActions={[
            { text: 'Yes - Same Patient', disabled: isLoading, onClick: mergePatients }
          ]}
          closeAction={{
            text: 'No - Different Patient',
            onClick: () => openDialog(resolveTicketsData, action),
            disabled: isLoading
          }}
        >
          <MergePatientsModalContent
            columns={columns}
            duplicatePatientData={duplicatePatientData}
          />
        </Modal>
      </>
    );
  }
);
